/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import {
  Switch,
  Route,
  useHistory,
  useParams,
  Redirect,
  useLocation,
} from 'react-router-dom';
import { useIntl } from 'react-intl';
import { message } from 'antd';
import ResizeObserver from 'resize-observer-polyfill';
import { MobileViewerHost } from '~components/atoms/Viewers/SVFViewer/MobileViewerHost';

import { EStyleObj } from '../../../types';
import {
  TableEntry,
  handleSelectCtrl,
  scrollToSelectedItem,
} from '~components/atoms/FileTables/FileTable.utils';
import SidebarContext from '~src/contexts/SidebarContext';
import { ProjectWebTable } from '~components/atoms/FileTables/ProjectWebTable';
import { SharedTenderAttachmentsTable } from '~components/atoms/FileTables/SharedTenderAttachmentsTable';
import { ProjectWebMyTendersTable } from '~components/atoms/FileTables/ProjectWebMyTendersTable';
import { MyTendersTabs } from '~components/molecules/Tabs/MyTenderTabs/MyTendersTabs';
import { FileTableProps } from '~components/atoms/FileTables/FileTable';
import {
  downloadFileLastVersion,
  downloadModuleFile,
  saveFromLink,
} from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { appLog } from '~src/utils/log';
import { debounce, uniqBy } from 'lodash';
import PropertiesPanel from '~components/atoms/PropertiesPanel/PropertiesPanel';
import { TenderPropertiesTable } from '~components/atoms/FileTables/TenderPropertiesTable';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './style';
import RightSideViewHome from '~components/molecules/RightSideViewHome/RightSideViewHome';
import { CachedSharesTable } from '~components/atoms/CachedSharesTable/ProjectWebCachedSharesTable';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import {
  GetTenderAttachmentResponse,
  requestFileLocation,
} from '~src/services/api';
import { DbModuleFile } from '~src/types/db';
import { parseFileName } from '~src/utils/file';
import Viewer from '~components/atoms/Viewers/Viewer';
import RightSideViewerContainer from '~components/atoms/Viewers/RightSideViewerContainer/RightSideViewerContainer';
import {
  MINIMIZED_RIGHT_BLOCK_WIDTH_CSS,
  RIGHT_BLOCK_WIDTH_CSS,
} from '~src/styles/constants';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import CachedSharesContext from '~src/contexts/CachedSharesContext';
import CherryPickPanel from '~components/atoms/CherryPickPanel/CherryPickPanel';
import {
  loadSharedTenderAttachment,
  loadSharedTenderVersionedFile,
} from '~src/services/api/shares';
import { downloadVendorTenderFolderFile } from '~src/utils/download';
import { CachedPublicTendersTable } from '~components/atoms/PublicTendersTable/ProjectWebCachedPublicTendersTable';
import CachedPublicTenderContext from '~src/contexts/CachedPublicTender';
import { PublicTenderAttachmentsTable } from '~components/atoms/PublicTendersTable/PublicTenderAttachmentsTable';

interface RightDisplayHomeProps {
  children: any;
  showProjectInfo?: boolean;
}

type ShareKeyEntryParams = {
  slug: string;
  compoundId: string;
};

// Entry component for email links:
// /share-keys/:slug/:shareId-shareKey
// 1) parse shareId + shareKey
// 2) set current cached share
// 3) redirect to /invitations/${shareId}-${shareKey}/details
const ShareKeyEntry: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const { slug, compoundId } = useParams<ShareKeyEntryParams>();
  const { setCurrentCachedShare } = useContext(CachedSharesContext);

  useEffect(() => {
    if (!compoundId) return;

    const parts = compoundId.split('-');
    if (parts.length < 2) {
      message.error(
        intl.formatMessage({ id: 'public-share-invalid-link' }) ||
          'This public link is invalid or has expired'
      );
      return;
    }

    const shareId = parts[0];
    const shareKey = parts.slice(1).join('-'); // key may contain '-' inside

    // Store current invitation in context so /invitations/:shareId can use it
    setCurrentCachedShare({
      shareId,
      shareKey,
    });

    // Redirect to the main invitation route
    history.replace(`/invitations/${shareId}-${shareKey}/details`);
  }, [compoundId, intl, history, setCurrentCachedShare]);

  // No UI – this route only performs redirect
  return null;
};

const RightDisplayHome = ({
  children,
  showProjectInfo,
}: RightDisplayHomeProps) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const customStyles = useStyles(styleFn);

  const pathSegments = history.location.pathname.split('/').filter(Boolean);
  const isOnMyTenders = pathSegments[0] === 'my-tenders';

  const shareIdKey = isOnMyTenders ? pathSegments[1] : '';
  const shareId = shareIdKey ? shareIdKey.split('-')[0] : '';
  const shareKey = shareIdKey ? shareIdKey.split('-').slice(1).join('-') : '';

  const { currentFolderId, rootFolderId } = useTenderParamsFromUrl({
    isOnShare: true,
  });
  const isFirstLevelDocs = !currentFolderId;

  const { fileVersionsContext } = useContext(FileVersionsContext);
  const {
    fileToView,
    setFileToView,
    loadingState,
    setLoadingState,
    viewerTopMargin,
  } = useFileViewerContext();
  const { isSidebarMinimized } = useContext(SidebarContext);

  const [selectedUrl, setSelectedUrl] = useState<string | undefined>();
  const [selectedForDetails, setSelectedForDetails] = useState<TableEntry>();
  const [selectedItems, setSelectedItems] = useState<TableEntry[]>([]);

  const [bottomViewHeight, setBottomViewHeight] = useState(0);
  const zoneDivRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef(
    new ResizeObserver(
      debounce(([resizeEntry]) => {
        // update `bottomViewHeight` based on "zoneDiv" height
        setBottomViewHeight(resizeEntry.contentRect.height);
      }, 50)
    )
  );

  const clickTimeoutRef = useRef<number>();

  // Clear selection on navigation
  useEffect(() => {
    // setSelectedItems([]);
    setSelectedForDetails(undefined);
  }, [history.location.pathname]);

  const handleFolderDoubleClick = (record: TableEntry) => {
    window.clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = undefined;

    // Primary-/Secondary Share Keys: block opening a share whose parent
    // inviter has been rejected/revoked (or own key removed/deleted/expired) -
    // mirrors backend's access-check logic, which would otherwise 403 here.
    if (record.disabled) {
      message.warning(
        intl.formatMessage({ id: 'tender-share-access-revoked' })
      );
      return;
    }

    // IMPORTANT:
    // record.key for MyTenders root table should already be shareIdKey now (shareId-shareKeyId)
    // so we route to /my-tenders/:shareIdKey
    history.push(`/my-tenders/${record.key}`);
  };

  const { rootFolderId: urlRootFolderId } = useTenderParamsFromUrl({
    isOnShare: true,
  });

  const handleDocumentsFolderDoubleClick = (record: TableEntry) => {
    const pathname = history.location.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);

    // IMPORTANT:
    // New URL format: /my-tenders/:shareIdKey/...
    const shareIdKey = pathSegments[1];

    // ми на my-tenders
    const isMyTenders = pathname.includes('/my-tenders/');
    // ми у корені документів my-tenders
    const isMyTendersDocsRoot = /^\/my-tenders\/[^/]+\/documents\/?$/.test(
      pathname
    );
    // ми вже всередині якогось кореня: /tender-folders/:root/folders/:id/documents
    const isMyTendersFolders = /\/tender-folders\/\d+\/folders\/\d+\/documents\/?$/.test(
      pathname
    );

    if (isMyTenders && (isMyTendersDocsRoot || isMyTendersFolders)) {
      // НОВА логіка: root з URL (якщо є), у folders/:id — саме клікнута папка
      const rootId = urlRootFolderId || record.firstElementId || record.id;

      history.push(
        `/my-tenders/${shareIdKey}/tender-folders/${rootId}/folders/${record.id}/documents`
      );
      return;
    }

    window.clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = undefined;

    if (record?.isUnassigned) {
      history.push(
        `/my-tenders/${shareIdKey}/tender-folders/${record.firstElementId}/folders/${record.id}/documents`
      );
    } else if (
      record.isOnTenderPropertyPanel &&
      record.dbTenderFolder?.options.tender_registration_id === null
    ) {
      history.push(
        `/my-tenders/${shareIdKey}/tender-folders/${record.id}/folders/${record.id}/documents`
      );
    } else {
      history.push(
        `/my-tenders/${shareIdKey}/tender-folders/${
          record?.dbTenderFolder?.options?.is_tender_root_folder
            ? record.id
            : record?.firstElementId
            ? record.firstElementId
            : record.dbTenderFolder?.parent_id
        }/folders/${record.id}/documents`
      );
    }
  };

  const handleFileDoubleClick = async (record: TableEntry) => {
    try {
      if (isOnMyTenders && shareId && shareKey) {
        if (record.dbAttachment) {
          if (isFirstLevelDocs) {
            // ROOT FOLDER: attachments
            const res = await loadSharedTenderAttachment({
              file_guid: record.dbAttachment.id,
              share_id: shareId,
              share_key: shareKey,
            });

            if (res?.location) {
              message.info(intl.formatMessage({ id: 'download-starting' }));
              saveFromLink(res.location);
              return;
            }
          } else {
            // VENDOR ROOT'S SUBFOLDERS
            const versionId = fileVersionsContext[record.id]?.id;
            const stableVersions = (record.dbAttachment.files || [])
              .map((f: any) => ({
                ...f,
                revision: f.serial_number ?? f.version ?? f.revision,
                version: f.serial_number ?? f.version ?? f.revision,
                id: f.registration_instance_id || f.file_id || f.id,
              }))
              .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

            const selectedVersion =
              stableVersions.find(
                (v: any) => String(v.id) === String(versionId)
              ) || stableVersions[0];

            if (!selectedVersion) {
              message.error(intl.formatMessage({ id: 'file-download-failed' }));
              return;
            }

            // VENDOR SUBFOLDER: download file
            await downloadVendorTenderFolderFile({
              shareId,
              shareKey,
              rootFolderId: String(rootFolderId),
              folderId: String(currentFolderId),
              registrationId: String(
                selectedVersion.registration_id || record.dbAttachment.id
              ),
              serialNumber: selectedVersion.version,
              onDownloadStarted: () =>
                message.info(intl.formatMessage({ id: 'download-starting' })),
            });
            return;
          }
        }

        if (record.dbFile && !isFirstLevelDocs) {
          // VENDOR SUBFOLDER (dbFile): Get selected version from context or fallback to latest
          const versionId = fileVersionsContext[record.id]?.id;
          const stableVersions = (record.dbFile.files || [])
            .map((f: any) => ({
              ...f,
              revision: f.serial_number ?? f.version ?? f.revision,
              version: f.serial_number ?? f.version ?? f.revision,
              id: f.registration_instance_id || f.file_id || f.id,
            }))
            .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

          const selectedVersion =
            stableVersions.find(
              (v: any) => String(v.id) === String(versionId)
            ) ||
            stableVersions[0] ||
            record.dbFile.versions?.find(
              (v: any) => String(v.id) === String(versionId)
            ) ||
            record.dbFile.versions?.[0];

          if (!selectedVersion) {
            message.error(intl.formatMessage({ id: 'file-download-failed' }));
            return;
          }

          // VENDOR SUBFOLDER
          await downloadVendorTenderFolderFile({
            shareId,
            shareKey,
            rootFolderId: String(rootFolderId),
            folderId: String(currentFolderId),
            registrationId: String(
              selectedVersion.registration_id || record.dbFile.id
            ),
            serialNumber: selectedVersion.version,
            onDownloadStarted: () =>
              message.info(intl.formatMessage({ id: 'download-starting' })),
          });
          return;
        }
      }

      if (record.dbFile) {
        downloadFileLastVersion(
          record.dbFile,
          fileVersionsContext[record.dbFile.id]?.id,
          () => message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
        return;
      }

      if (record.moduleFile && record.dbModuleFile) {
        downloadModuleFile(record.dbModuleFile, () =>
          message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }
    } catch (err) {
      appLog('vendor double click download -> err', err);
      message.error(intl.formatMessage({ id: 'file-download-failed' }));
    }
  };

  const toggleItemSelected = (item: TableEntry) => {
    setSelectedItems((prevItems) => {
      const nextItems = [...prevItems];
      const index = nextItems.findIndex((v) => v.key === item.key);

      if (index > -1) {
        // found -> remove item
        nextItems.splice(index, 1);
      } else {
        nextItems.unshift(item); // insert at start of array
      }

      return nextItems;
    });
  };

  const handleRowClick = (record: TableEntry) => {
    if (!selectedItems.length) {
      return setSelectedForDetails((prevRecord) =>
        prevRecord?.key === record.key ? undefined : record
      );
    }

    if (!record.disabled) {
      toggleItemSelected(record);
    }
  };

  const handleRow: Required<FileTableProps>['onRow'] = (record) => {
    if (record.isOnTenderPropertyPanel)
      return {
        onDoubleClick: () => handleDocumentsFolderDoubleClick(record),
      };
    return {
      onClick: (event) => {
        if (event.ctrlKey || event.metaKey) {
          return handleSelectCtrl({ record, setSelectedItems, selectedItems });
        }
        return handleRowClick(record);
      },
      onDoubleClick: record.folder
        ? () => {
            if (record?.isTenderDocuments) {
              handleDocumentsFolderDoubleClick(record);
            } else {
              handleFolderDoubleClick(record);
            }
          }
        : () => handleFileDoubleClick(record),
    };
  };

  let panelNode: React.ReactElement | undefined;

  const isOnDetails = location.pathname.endsWith('/details');
  const isOnContract = location.pathname.endsWith('/contract');
  const isOnMovements = location.pathname.endsWith('/movements');
  const isOnQuestions = location.pathname.endsWith('/questions');
  const isOnHelp = location.pathname.endsWith('/help');

  const shouldHidePanel =
    isOnDetails || isOnContract || isOnMovements || isOnQuestions || isOnHelp;

  // --- Selection Handlers ---
  const handleSelect = (record: TableEntry, selected: boolean) => {
    if (selectedForDetails) setSelectedForDetails(undefined);
    if (selected) {
      // Insert at start
      setSelectedItems((prev) => [record, ...prev]);
    } else {
      // Remove
      setSelectedItems((prev) =>
        prev.filter((item) => item.key !== record.key)
      );
    }
  };

  const handleSelectAll = (
    selected: boolean,
    rows: TableEntry[],
    changedRows: TableEntry[]
  ) => {
    if (selectedForDetails) setSelectedForDetails(undefined);
    // Filter truthy values
    const selectedRows = rows.filter(Boolean);
    if (selected) {
      // Merge and uniq
      setSelectedItems((prev) => uniqBy([...selectedRows, ...prev], 'key'));
    } else {
      // Remove deselected
      const rowKeys = changedRows.map((r) => r.key);
      setSelectedItems((prev) =>
        prev.filter((item) => !rowKeys.includes(item.key))
      );
    }
  };

  if (shouldHidePanel) {
    panelNode = undefined;
  } else if (selectedItems.length) {
    panelNode = (
      <CherryPickPanel
        key="cherry-picker"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    );
  } else if (selectedForDetails) {
    panelNode = (
      <PropertiesPanel
        tenderPropertiesTable={
          <TenderPropertiesTable
            highlightedRow={selectedForDetails}
            handleRow={handleRow}
            setSelectedItems={setSelectedItems}
          />
        }
        key="properties"
        selectedItem={selectedForDetails}
      />
    );
  }

  useEffect(() => {
    if (selectedForDetails?.key) {
      scrollToSelectedItem(selectedForDetails.key);
    }
  }, [selectedForDetails?.key]);

  useEffect(() => {
    const div = zoneDivRef.current;

    if (div) {
      const resizeObserver = resizeObserverRef.current; // ResizeObserver instance
      resizeObserver.observe(div); // add listener

      // cleanup -> remove listener
      return () => resizeObserver.unobserve(div);
    }
  }, [panelNode?.key]);

  const tableScroll: Required<FileTableProps>['scroll'] = {
    // satisfies sum of fixed width columns + reasonable dynamic "name" column
    x: 1065,
    // 141px is topBar + folderNameBar + tableHeader + scrollbar
    y: `calc(100vh - 132px - ${bottomViewHeight}px)`,
  };

  const resetSelectedForDetails = () => setSelectedForDetails(undefined);

  const selectedKeys: Required<FileTableProps>['selectedKeys'] = selectedItems.map(
    (item) => item.key
  );

  const isGetTenderAttachmentResponse = (
    obj: any
  ): obj is GetTenderAttachmentResponse => {
    return obj && obj.location !== undefined;
  };

  const isVendorShareRootSVFSource = (file?: any) =>
    !!file && file.sourceType === 'tender-share-root-svf';

  const isVendorShareFolderSVFSource = (file?: any) =>
    !!file && file.sourceType === 'tender-share-inside-svf';

  const isVendorSVFSource = (file?: any) =>
    isVendorShareRootSVFSource(file) || isVendorShareFolderSVFSource(file);

  useEffect(() => {
    if (isVendorSVFSource(fileToView)) {
      setSelectedUrl(undefined);
      return;
    }

    if (
      !isGetTenderAttachmentResponse(fileToView) &&
      fileToView &&
      (fileToView.hasOwnProperty('versions') ||
        fileToView.hasOwnProperty('location'))
    ) {
      const selectedIndex = fileVersionsContext[fileToView.id]
        ? fileToView.versions?.findIndex(
            (opt) => opt.id === fileVersionsContext[fileToView.id]?.id
          )
        : 0;

      if ((selectedIndex as number) >= 0) {
        const getFileToViewLocation = async () => {
          if (fileToView.versions) {
            const getFileLocation = await requestFileLocation({
              fileId: fileToView.versions[selectedIndex as number].id,
              projectId:
                fileToView.versions[selectedIndex as number].project_id,
              versionedFileId:
                fileToView.versions[selectedIndex as number].versioned_file_id,
            });
            return getFileLocation;
          }
        };

        const fileLocation = getFileToViewLocation();
        const setFileLocation = async () => {
          setSelectedUrl(await fileLocation);
        };

        setFileLocation();
      }
    } else if (isGetTenderAttachmentResponse(fileToView)) {
      setSelectedUrl(fileToView.location);
    } else {
      setSelectedUrl(undefined);
    }
  }, [fileToView, fileVersionsContext]);

  const renderWithFileViewer = (content: JSX.Element) => {
    const isVendorSVF = isVendorSVFSource(fileToView);

    if (
      fileToView &&
      (isVendorSVF ||
        (!isGetTenderAttachmentResponse(fileToView) && fileToView?.versions) ||
        (fileToView as DbModuleFile).location ||
        isGetTenderAttachmentResponse(fileToView)) &&
      (isVendorSVF || selectedUrl)
    ) {
      // Start of processing the file for preview, operation is sync, so we assume this as the exact beginning
      const { extension } = parseFileName(
        fileToView?.['title']
          ? fileToView?.['title']
          : fileToView?.['file_name']
      );

      return (
        <RightSideViewerContainer
          bottomViewHeight={bottomViewHeight}
          MainContent={content}
          ViewerItem={
            <Viewer
              fileToView={fileToView as any}
              onCloseSVFViewer={() => setFileToView(undefined)}
              fileUrl={selectedUrl || ''}
              fileExtension={extension}
            />
          }
          isViewerLoading={isVendorSVF ? false : !selectedUrl}
          viewerTopMargin={viewerTopMargin}
        />
      );
    }

    return content;
  };

  return (
    <div
      css={{
        ...(customStyles.container as EStyleObj),
        width: isSidebarMinimized
          ? MINIMIZED_RIGHT_BLOCK_WIDTH_CSS
          : RIGHT_BLOCK_WIDTH_CSS,
      }}
      onContextMenu={(e) => {
        e.preventDefault();
      }}>
      {window.location.pathname === '/' ? (
        <RightSideViewHome
          showProjectInfo={showProjectInfo && window.location.pathname !== '/'}>
          {children}
          {/* VIEWER HOME PAGE WRAPPER */}
          {(() => {
            const ext = fileToView
              ? parseFileName(
                  (fileToView as any)?.title ||
                    (fileToView as any)?.file_name ||
                    (fileToView as any)?.name ||
                    ''
                ).extension
              : '';

            const is3D = [
              'svf',
              'svf2',
              'rvt',
              'ifc',
              'nwd',
              'nwc',
              'dwg',
            ].includes((ext || '').toLowerCase());

            const sourceType = (fileToView as any)?.sourceType;
            const isBridgedSVF =
              sourceType === 'tender-admin-root-svf' ||
              sourceType === 'tender-admin-inside-svf' ||
              sourceType === 'tender-unassigned-inside-svf' ||
              sourceType === 'tender-share-root-svf' ||
              sourceType === 'tender-share-inside-svf';

            return is3D && !isBridgedSVF ? <MobileViewerHost /> : null;
          })()}
        </RightSideViewHome>
      ) : (
        <Fragment>
          {/* VIEWER HOME PAGE WRAPPER */}
          {(() => {
            const ext = fileToView
              ? parseFileName(
                  (fileToView as any)?.title ||
                    (fileToView as any)?.file_name ||
                    (fileToView as any)?.name ||
                    ''
                ).extension
              : '';

            const is3D = [
              'svf',
              'svf2',
              'rvt',
              'ifc',
              'nwd',
              'nwc',
              'dwg',
            ].includes((ext || '').toLowerCase());

            const sourceType = (fileToView as any)?.sourceType;
            const isBridgedSVF =
              sourceType === 'tender-admin-root-svf' ||
              sourceType === 'tender-admin-inside-svf' ||
              sourceType === 'tender-unassigned-inside-svf' ||
              sourceType === 'tender-share-root-svf' ||
              sourceType === 'tender-share-inside-svf';

            return is3D && !isBridgedSVF ? <MobileViewerHost /> : null;
          })()}
          <Switch>
            {/* Invitations list */}
            <Route exact path="/invitations" component={CachedSharesTable} />

            {/* Invitation details (public shared tender view) */}
            <Route
              path="/invitations/:tenderId"
              render={() => (
                <SharedTenderAttachmentsTable
                  handleRow={handleRow}
                  selectedKeys={selectedKeys}
                  setSelectedItems={setSelectedItems}
                  highlightedRow={selectedForDetails}
                  resetSelectedForDetails={resetSelectedForDetails}
                  scroll={tableScroll}
                />
              )}
            />

            {/* Public tenders list */}
            <Route
              exact
              path="/public-tenders"
              component={CachedPublicTendersTable}
            />

            {/* Public tender view */}
            <Route
              path="/public-tenders/:tenderShareId"
              render={() => (
                <PublicTenderAttachmentsTable
                  handleRow={handleRow}
                  selectedKeys={selectedKeys}
                  setSelectedItems={setSelectedItems}
                  highlightedRow={selectedForDetails}
                  resetSelectedForDetails={resetSelectedForDetails}
                  scroll={tableScroll}
                />
              )}
            />

            {/* Entry from email link: /share-keys/:slug/:shareId-shareKey */}
            <Route
              path="/share-keys/:slug/:compoundId"
              render={() => <ShareKeyEntry />}
            />

            <Route
              exact
              path="/my-tenders"
              render={() =>
                renderWithFileViewer(
                  <ProjectWebMyTendersTable
                    handleRow={handleRow}
                    setSelectedItems={setSelectedItems}
                    highlightedRow={selectedForDetails}
                    selectedKeys={selectedKeys}
                  />
                )
              }
            />

            <Route
              path="/my-tenders/:shareIdOnly([^/\\-]+)/:tab"
              render={() => <Redirect to="/my-tenders" />}
            />

            {/* IMPORTANT:
                Use shareIdKey everywhere for MyTenders routes */}
            <Route
              path="/my-tenders/:shareIdKey/tender-folders/:rootFolderId/folders/:currentFolderId"
              render={() =>
                renderWithFileViewer(
                  <MyTendersTabs
                    handleRow={handleRow}
                    selectedKeys={selectedKeys}
                    onSelect={handleSelect}
                    onSelectAll={handleSelectAll}
                    setSelectedItems={setSelectedItems}
                    highlightedRow={selectedForDetails}
                    resetSelectedForDetails={resetSelectedForDetails}
                    scroll={tableScroll}
                    bottomViewHeight={bottomViewHeight}
                  />
                )
              }
            />
            <Route
              path="/my-tenders/:shareIdKey"
              render={() =>
                renderWithFileViewer(
                  <MyTendersTabs
                    handleRow={handleRow}
                    selectedKeys={selectedKeys}
                    onSelect={handleSelect}
                    onSelectAll={handleSelectAll}
                    setSelectedItems={setSelectedItems}
                    highlightedRow={selectedForDetails}
                    resetSelectedForDetails={resetSelectedForDetails}
                    scroll={tableScroll}
                    bottomViewHeight={bottomViewHeight}
                  />
                )
              }
            />

            <Route exact path="/my-projects" component={ProjectWebTable} />
            <Route
              exact
              path="/my-projects/:projectId"
              render={(props) => (
                <RightSideViewHome
                  {...props}
                  showProjectInfo={showProjectInfo}
                  children={children}
                />
              )}
            />
            {/* <Redirect to="/my-projects" /> */}
          </Switch>

          <div css={customStyles.panelsContainer} ref={zoneDivRef}>
            {panelNode}
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default RightDisplayHome;
